/**
 * Modern Bold · <mb-cart-discount> — discount codes in the cart drawer.
 *
 * DOM contract (snippets/modern-cart-drawer.liquid):
 *   data-codes              codes already on the cart, comma-separated
 *   details > summary       the "Discount code" row, set like the order note;
 *                           it opens the field below it and closes it again
 *   form > input            the code field
 *   form > [type=submit]    "Apply"
 *   [role="alert"]          error line
 *   [role="status"]         visually hidden confirmation
 *   [data-discount-remove]  anywhere in the drawer (ticket, line tag): removes
 *                           the code it names
 *
 * /cart/update.js's `discount` field replaces the cart's whole code list, so
 * applying sends the existing codes plus the new one and removing sends the
 * rest. A code the cart can't use still comes back 200, flagged
 * `applicable: false`. The drawer re-renders from the same response (its own
 * section helpers), which replaces this element; the new one reads the result
 * from `pending`.
 */
if (!customElements.get('mb-cart-discount')) {
  let pending = null;
  const same = (a, b) => a.toLowerCase() === b.toLowerCase();

  class MbCartDiscount extends HTMLElement {
    #details;
    #form;
    #input;
    #button;
    #busy = false;

    connectedCallback() {
      this.#details = this.querySelector('details');
      this.#form = this.querySelector('form');
      this.#input = this.querySelector('input');
      this.#button = this.querySelector('[type="submit"]');
      this.#details?.addEventListener('toggle', this.#onToggle);
      this.#form?.addEventListener('submit', this.#onSubmit);
      this.#input?.addEventListener('input', this.#onInput);
      document.addEventListener('click', this.#onRemoveClick);

      if (pending) {
        const result = pending;
        pending = null;
        this.#report(result);
      }
    }

    disconnectedCallback() {
      this.#details?.removeEventListener('toggle', this.#onToggle);
      this.#form?.removeEventListener('submit', this.#onSubmit);
      this.#input?.removeEventListener('input', this.#onInput);
      document.removeEventListener('click', this.#onRemoveClick);
    }

    // Opening the row means the customer is about to type: focus the field.
    // Closing hides it; focus left inside (Safari doesn't focus a clicked
    // summary) goes back to the row.
    #onToggle = () => {
      if (this.#details.open) {
        this.#focusField();
        return;
      }
      if (this.#details.contains(document.activeElement)) this.querySelector('summary')?.focus();
      // A closed row drops its error, so reopening starts clean.
      this.classList.remove('is-invalid');
      this.querySelector('[role="alert"]').textContent = '';
    };

    #onInput = () => {
      this.classList.remove('is-invalid');
    };

    // The row's content animates in from content-visibility: hidden and can't
    // take focus for the first frame or two (measured: two).
    #focusField(tries = 5) {
      this.#input.focus();
      if (document.activeElement !== this.#input && tries > 0) {
        requestAnimationFrame(() => this.#focusField(tries - 1));
      }
    }

    #codes() {
      return (this.dataset.codes ?? '').split(',').filter(Boolean);
    }

    #onSubmit = async (event) => {
      event.preventDefault();
      const code = this.#input.value.trim();
      if (!code || this.#busy) return;

      const codes = this.#codes();
      if (codes.some((c) => same(c, code))) {
        this.#report({ code, error: `${code} is already applied.` });
        return;
      }

      this.#setBusy(true);
      try {
        const cart = await this.#update([...codes, code]);
        const rejected = cart.discount_codes?.some((d) => same(d.code, code) && !d.applicable);
        this.#render(cart, rejected ? { code, error: `${code} can’t be applied to this cart.` } : { status: `${code} applied.` });
      } catch (error) {
        this.#setBusy(false);
        this.#report({ code, error: error.message || 'Something went wrong. Please try again.' });
      }
    };

    #onRemoveClick = async (event) => {
      const button = event.target.closest('[data-discount-remove]');
      if (!button || !button.closest('cart-drawer') || this.#busy) return;

      const code = button.dataset.discountRemove;
      this.#setBusy(true);
      button.setAttribute('aria-disabled', 'true');
      try {
        const cart = await this.#update(this.#codes().filter((c) => !same(c, code)));
        this.#render(cart, { status: `${code} removed.` });
      } catch (error) {
        this.#setBusy(false);
        button.removeAttribute('aria-disabled');
        this.#report({ error: error.message || 'The code couldn’t be removed. Please try again.' });
      }
    };

    async #update(codes) {
      const res = await fetch(`${window.routes?.cart_update_url ?? '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: codes.join(','),
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || cart.message || '');
      return cart;
    }

    #render(cart, result) {
      pending = result;

      // renderContents() minus its open(): on a drawer that's already open,
      // open() leaves a transitionend listener that later pulls focus to the panel.
      const drawer = document.querySelector('cart-drawer');
      drawer?.getSectionsToRender().forEach(({ id, selector }) => {
        const target = selector ? document.querySelector(selector) : document.getElementById(id);
        if (target && cart.sections?.[id]) target.innerHTML = drawer.getSectionInnerHTML(cart.sections[id], selector);
      });

      // No drawer to re-render: report here instead.
      if (this.isConnected && pending) {
        pending = null;
        this.#setBusy(false);
        this.#report(result);
      }
    }

    #setBusy(on) {
      this.#busy = on;
      this.#button?.setAttribute('aria-disabled', String(on));
    }

    // An error reopens the field with the code in it, to fix. Success leaves
    // it closed and focuses the row, so a phone doesn't reopen its keyboard.
    #report({ code = '', error = '', status = '' }) {
      this.classList.toggle('is-invalid', Boolean(error));
      if (error) {
        if (this.#details) this.#details.open = true;
        this.#input.value = code;
        this.#focusField();
      } else {
        this.querySelector('summary')?.focus();
      }
      // A beat later, so screen readers catch the change in fresh live regions.
      setTimeout(() => {
        this.querySelector('[role="alert"]').textContent = error;
        this.querySelector('[role="status"]').textContent = status;
      }, 100);
    }
  }

  customElements.define('mb-cart-discount', MbCartDiscount);
}
